import { aclFeatures } from '@/lib/acl/aclFeatures';
import {
    ANALYZE_COMMUNITY,
    ANALYZE_MENTIONS,
    ANALYZE_OVERVIEW,
    LISTENING_COMPETITORS,
    LISTENING_COMPETITOR,
    LISTENING_HASHTAGS,
    LISTENING_HASHTAG,
    DASHBOARD,
    DASHBOARD_VIEW,
    DASHBOARD_CUSTOMIZATION,
    DASHBOARD_GENERATE_CUSTOM_REPORT,
    ANALYZE_ENGAGEMENT,
    LISTENING_FEEDS,
    LISTENING_FEED,
    ANALYZE_INDUSTRY_BENCHMARK,
    CONTENT_POSTS,
    ANALYZE_REACH_IMPRESSIONS,
    REPORTS,
    SCHEDULER,
    ANALYZE_CONTENT,
    ANALYZE_PERFORMANCE,
    CONTENT_ALBUMS,
    MANAGE_COMMENTS_TRACKER,
    SCHEDULER_COLLABORATION,
    SCHEDULER_COLLABORATION_SETTINGS,
    SCHEDULER_COLLABORATION_NOTIFICATIONS,
    MEDIA_LIBRARY,
    INSPIRATION,
    ACCOUNT,
    SOCIAL_PROFILES,
    NOTIFICATIONS,
    HOME,
    SUBSCRIPTION_INVOICES,
    PLATFORM_OPTIONS,
    THEMES,
    SECURITY,
    DATA_ACCESS,
    INTEGRATIONS,
    USERS_ROLES,
    REFERRAL_PROGRAM,
    CAPTIONS_LISTS
} from '@/routes/privateRoutes';

type MenuEntryDefaultType = {
    id: string;
    name: string;
    urlActiveMenu?: string[];
    icon: string;
};

type MenuAclType = {
    acl?: {
        permissions_plan_restriction?: string[];
    };
};

type MenuEntryLinkType = MenuAclType & {
    link: string;
};

type MenuEntryDisclosureType = {
    subMenuItems: SubMenuEntryType[];
};

type MenuEntryDisplaySubMenuMobileType = MenuAclType & {
    link: string;
    displaySubMenuMobile: true;
};

export type SubMenuEntryType = Omit<MenuEntryDefaultType, 'icon'> &
    (MenuEntryLinkType | MenuEntryDisplaySubMenuMobileType);

export type MenuEntryWithSubEntriesType = MenuEntryDefaultType & MenuEntryDisclosureType;

export type MenuEntryType = MenuEntryDefaultType &
    (MenuEntryLinkType | MenuEntryDisclosureType | MenuEntryDisplaySubMenuMobileType);

export type MenuEntryDropdownType = MenuEntryDefaultType &
    (MenuEntryDisclosureType | MenuEntryDisplaySubMenuMobileType);

export const mainMenu: MenuEntryType[] = [
    // Home
    {
        id: 'home',
        icon: 'explore',
        link: HOME,
        name: 'commons:menu.home',
        urlActiveMenu: [HOME]
    },
    // Dashboard
    {
        id: 'dashboard',
        icon: 'dashboard',
        link: DASHBOARD,
        name: 'commons:menu.dashboard',
        urlActiveMenu: [DASHBOARD_VIEW, DASHBOARD_CUSTOMIZATION, DASHBOARD_GENERATE_CUSTOM_REPORT]
    },
    // Analytics
    {
        id: 'analytics',
        icon: 'analytics',
        name: 'menu.analytics',
        subMenuItems: [
            {
                id: 'analyze-overview',
                link: ANALYZE_OVERVIEW,
                name: 'commons:menu.overview'
            },
            {
                id: 'analyze-engagement',
                link: ANALYZE_ENGAGEMENT,
                name: 'commons:menu.engagement'
            },
            {
                id: 'analyze-community',
                link: ANALYZE_COMMUNITY,
                name: 'commons:menu.community'
            },
            {
                id: 'analyze-reach-impressions',
                link: ANALYZE_REACH_IMPRESSIONS,
                name: 'commons:menu.reach_impressions'
            },
            {
                id: 'analyze-content',
                link: ANALYZE_CONTENT,
                name: 'commons:menu.content'
            },
            {
                id: 'analyze-performance',
                link: ANALYZE_PERFORMANCE,
                name: 'commons:menu.profile_performance'
            },
            {
                id: 'analyze-mentions',
                link: ANALYZE_MENTIONS,
                name: 'commons:menu.mentions'
            }
        ]
    },
    // Reports
    {
        id: 'reports',
        icon: 'reports',
        link: REPORTS,
        name: 'commons:menu.exports_reports'
    },
    // Content
    {
        id: 'content',
        icon: 'content',
        name: 'commons:menu.content',
        subMenuItems: [
            {
                id: 'content-posts',
                link: CONTENT_POSTS,
                name: 'commons:menu.all_posts'
            },
            {
                id: 'content-albums',
                link: CONTENT_ALBUMS,
                name: 'commons:menu.campaigns',
                acl: {
                    permissions_plan_restriction: [aclFeatures.content.campaign.read]
                }
            }
        ]
    },
    // Publishing
    {
        id: 'publishing',
        icon: 'publishing',
        name: 'commons:menu.publishing',
        subMenuItems: [
            {
                id: 'publishing-scheduler',
                link: SCHEDULER,
                name: 'commons:menu.calendar_view'
            },
            {
                id: 'publishing-collaboration',
                link: SCHEDULER_COLLABORATION,
                name: 'commons:menu.collaboration',
                urlActiveMenu: [
                    SCHEDULER_COLLABORATION_SETTINGS,
                    SCHEDULER_COLLABORATION_NOTIFICATIONS
                ],
                acl: {
                    permissions_plan_restriction: [
                        aclFeatures.publishing.external_collaboration.read
                    ]
                }
            },
            {
                id: 'publishing-media-library',
                link: MEDIA_LIBRARY,
                name: 'commons:menu.media_library'
            },
            {
                id: 'publishing-caption-template',
                link: CAPTIONS_LISTS,
                name: 'commons:menu.caption_template'
            }
        ]
    },
    // AI Content Inspiration
    {
        id: 'ai-content-inspiration',
        icon: 'inspiration',
        link: INSPIRATION,
        name: 'commons:menu.inspiration'
    },
    // Comments
    {
        id: 'comments',
        icon: 'comments',
        link: MANAGE_COMMENTS_TRACKER,
        name: 'commons:menu.conversations'
    },
    // Listening
    {
        id: 'listening',
        icon: 'listening',
        name: 'commons:menu.listening',
        subMenuItems: [
            {
                id: 'listening-competitors',
                link: LISTENING_COMPETITORS,
                name: 'commons:menu.competitors',
                urlActiveMenu: [LISTENING_COMPETITOR]
            },
            {
                id: 'listening-hashtags',
                link: LISTENING_HASHTAGS,
                name: 'commons:menu.hashtags',
                urlActiveMenu: [LISTENING_HASHTAG],
                acl: {
                    permissions_plan_restriction: [aclFeatures.listening.hashtag.read]
                }
            },
            {
                id: 'listening-feeds',
                link: LISTENING_FEEDS,
                name: 'commons:menu.feeds',
                urlActiveMenu: [LISTENING_FEED],
                acl: {
                    permissions_plan_restriction: [aclFeatures.listening.feed.read]
                }
            },
            {
                id: 'listening-industry-benchmark',
                link: ANALYZE_INDUSTRY_BENCHMARK,
                name: 'commons:menu.industry_benchmark'
            }
        ]
    }
];

export const bottomMenu: MenuEntryType[] = [
    // Notifications
    {
        id: 'notifications',
        icon: 'notification',
        link: NOTIFICATIONS,
        name: 'commons:menu.notifications'
    },
    // Social profiles
    {
        id: 'social-profiles',
        icon: 'single-profile',
        link: SOCIAL_PROFILES,
        name: 'commons:menu.social_profiles'
    },
    // Settings
    {
        id: 'settings',
        icon: 'account',
        link: ACCOUNT,
        name: 'commons:menu.my_account',
        urlActiveMenu: [
            ACCOUNT,
            PLATFORM_OPTIONS,
            THEMES,
            SECURITY,
            SUBSCRIPTION_INVOICES,
            DATA_ACCESS,
            INTEGRATIONS,
            USERS_ROLES,
            REFERRAL_PROGRAM
        ],
        displaySubMenuMobile: true
    }
];
